import React, { useState, useCallback } from 'react';
import { DropdownMenuItem } from '../../ui/dropdown-menu';
import { useLazyCommand, useEvent, useDomainEvent } from '@soap/modules';
import { Mail, X } from 'lucide-react';
import { useToast } from '../../ui/use-toast';

interface ResendOrgInviteDialogProps {
  inviteId: string;
  email: string;
}

export function ResendOrgInviteDialog({ inviteId, email }: ResendOrgInviteDialogProps) {
  const [open, setOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const [sendCommand] = useLazyCommand('C284v1_ResendOrgUserInvitation');
  const triggerCloseContextMenu = useDomainEvent({
    eventName: "close-context-menu",
  });
  const { toast } = useToast();

  const close = useCallback(() => {
    setOpen(false);
    triggerCloseContextMenu();
  }, [triggerCloseContextMenu]);

  // Listen for successful invitation event
  useEvent({
    eventName: "E201v1_OrgUserInvited",
    onEventReceived: () => {
      if (isSubmitting) {
        toast({
          title: "Invitation resent",
          description: `An invitation has been resent to ${email}.`,
        });
        close();
        setIsSubmitting(false);
      }
    }
  });

  const handleSubmit = async () => {
    setIsSubmitting(true);
    try {
      await sendCommand({
        C284_OrgUserInvitationId: inviteId
      });
      // Don't close here - wait for the event to show toast first
    } catch (error) {
      console.error('Failed to resend invitation:', error);
      setIsSubmitting(false);
    }
  };

  if (!open) {
    return (
      <DropdownMenuItem
        className="w-full justify-start"
        data-dialog-item
        onClick={() => setOpen(true)}
      >
        <Mail className="w-4 h-4 mr-2" />
        Resend
      </DropdownMenuItem>
    );
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-lg w-full mx-4 max-h-[90vh] overflow-y-auto">
        <div className="p-5">
          <div className="flex items-center justify-between mb-5">
            <h2 className="text-2xl font-bold text-gray-900">Resend User Invitation</h2>
            <button
              onClick={close}
              className="text-gray-400 hover:text-gray-600 transition-colors"
              disabled={isSubmitting}
            >
              <X className="w-6 h-6" />
            </button>
          </div>

          <div className="space-y-4">
            <p className="text-gray-700">
              Are you sure you want to resend the invitation email to <strong>{email}</strong>?
            </p>
            
            <div className="bg-action-blue/10 p-4 rounded-lg border border-action-blue/30">
              <h4 className="font-medium text-action-blue-dark mb-2">What happens when you resend an invitation?</h4>
              <ul className="ml-4 space-y-1">
                <li className="text-sm text-action-blue-dark">• A new invitation email will be sent to the user</li>
                <li className="text-sm text-action-blue-dark">• The existing invitation link will remain valid</li>
                <li className="text-sm text-action-blue-dark">• The user can use either invitation link to accept</li>
              </ul>
            </div>
          </div>

          <div className="flex justify-end space-x-3 pt-5">
            <button
              type="button"
              onClick={close}
              disabled={isSubmitting}
              className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleSubmit}
              disabled={isSubmitting}
              className="px-6 py-2 bg-action-blue hover:bg-action-blue-dark text-white rounded-lg transition-colors disabled:bg-gray-300 disabled:cursor-not-allowed min-w-[140px]"
            >
              {isSubmitting ? "Sending..." : "Resend Invitation"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

